import { logger } from "../services/logger.ts";
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MarketCard } from "../components/MarketCard.tsx";
import { FeaturedMarketCard } from "../components/FeaturedMarketCard.tsx";
import { Market, MarketCategory } from "../types.ts";
import { MarketEngine } from "../lib/marketEngine.ts";
import { useAuth } from "../lib/auth.tsx";
import { supabase, checkSupabaseConnection } from "../lib/db.ts";
import { Search, ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Category as CategoryType, fetchMarketCategories } from "../services/categoryService.ts";
import { Leaderboard } from "../components/Leaderboard.tsx";
import { AffiliateLeaderboard } from "../components/AffiliateLeaderboard.tsx";
import { usePromoConfig } from "../hooks/usePromoConfig.ts";
import { colors } from "../config/brand.ts";
import { SEO } from "../components/SEO.tsx";
// Button styles for category / sort pills
const activeButtonStyle = {
  backgroundColor: colors.primary.teal,
  color: 'white',
};
const inactiveButtonStyle = {
  backgroundColor: colors.secondary.slate,
  color: 'white',
};

const marketEngine = new MarketEngine();

// Performance optimizations
const MARKETS_PER_PAGE = 24;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const DEBOUNCE_DELAY = 300;

// Cache for market data
const marketCache = new Map<string, { data: Market[], timestamp: number }>();

// Debounce hook for search
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function Dashboard() {
  // Optimize state structure to reduce re-renders
  const [marketData, setMarketData] = useState({
    publicMarkets: [] as Market[],
    featuredMarkets: [] as Market[],
  });

  const [uiState, setUiState] = useState({
    loading: true,
    connectionError: false,
    searchQuery: '',
    selectedCategory: null as MarketCategory | null,
    sortBy: 'volume' as 'newest' | 'volume' | 'ending',
    showWatchlistOnly: false,
    showFilters: false,
    currentPage: 1,
    hasMore: true
  });

  const [activeCarouselIndex, setActiveCarouselIndex] = useState(0);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  const [categories, setCategories] = useState<CategoryType[]>([]);
  
  const { user } = useAuth();
  const { promoConfig } = usePromoConfig();

  // Debounced search query
  const debouncedSearchQuery = useDebounce(uiState.searchQuery, DEBOUNCE_DELAY);

  // Optimized cache key generator
  const getCacheKey = useCallback((category: string | null, userId: string | null, searchQuery: string) => {
    return `public_active_${category || 'all'}_${userId || 'anonymous'}_${searchQuery || 'no-search'}`;
  }, []);

  // Fetch user's watchlist
  const fetchUserWatchlist = useCallback(async (): Promise<Set<string>> => {
    if (!user) return new Set();
    
    try {
      const { data, error } = await supabase
        .from('watchlists')
        .select('market_id')
        .eq('user_id', user.id);
      
      if (error) throw error;
      
      return new Set((data || []).map(item => item.market_id));
    } catch (error) {
      logger.error('Error fetching watchlist:', error);
      return new Set();
    }
  }, [user]);

  // Optimized market fetching with pagination and caching
  const fetchMarketsOptimized = useCallback(async (page: number = 1, reset: boolean = false) => {
    try {
      if (uiState.connectionError) return;

      const cacheKey = getCacheKey(
        uiState.selectedCategory,
        user?.id || null,
        debouncedSearchQuery
      );
      const cached = marketCache.get(cacheKey);

      const shouldUseCache = page === 1 && !reset;

      if (shouldUseCache && cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        const activeCached = cached.data.filter((m) => m.status === 'active');
        setMarketData(prev => ({
          ...prev,
          publicMarkets: activeCached
        }));
        setUiState(prev => ({ ...prev, loading: false }));
        return;
      }

      const offset = (page - 1) * MARKETS_PER_PAGE;

      // Build optimized query with fields that exist in markets table
      let query = supabase
        .from('markets')
        .select(`
          id,
          title,
          description,
          category,
          status,
          is_private,
          is_verified,
          is_featured,
          created_at,
          resolution_time,
          image_url,
          created_by,
          is_dynamic,
          total_bets,
          volume,
          total_pool,
          updated_at,
          unique_traders,
          price_change_24h,
          winning_option_id,
          engine_type,
          lmsr_b,
          market_options!market_id(id, name, status, created_at, updated_at, outstanding_shares, image_url),
          market_pools(total_amount, market_option_id)
        `, { count: 'exact' })
        .eq('is_private', false)
        .eq('is_verified', true)
        .eq('status', 'active');

      // Apply search filter at database level if search query exists
      if (debouncedSearchQuery) {
        query = query.or(`title.ilike.%${debouncedSearchQuery}%,description.ilike.%${debouncedSearchQuery}%`);
      }

      if (uiState.selectedCategory) {
        query = query.eq('category', uiState.selectedCategory);
      }

      // Apply sorting at database level
      if (uiState.sortBy === 'volume') {
        // Sort by total_bets as proxy for volume (total_volume column doesn't exist)
        query = query.order('total_bets', { ascending: false, nullsFirst: false });
      } else if (uiState.sortBy === 'newest') {
        query = query.order('created_at', { ascending: false });
      } else if (uiState.sortBy === 'ending') {
        // For ending soon, sort by resolution_time ascending (closest first); undated last
        query = query.order('resolution_time', { ascending: true, nullsFirst: false });
      }

      // Apply pagination after sorting
      query = query.range(offset, offset + MARKETS_PER_PAGE - 1);

      const { data: publicData, error: publicError } = await query;

      if (publicError) throw publicError;

      // Sort raw data by total_volume from market_details view when volume sort is selected
      let sortedData = publicData || [];
      if (uiState.sortBy === 'volume' && sortedData.length > 0) {
        // Fetch total_volume from market_details view for these markets
        const marketIds = sortedData.map((m: any) => m.id);
        const { data: volumeData } = await supabase
          .from('market_details')
          .select('id, total_volume')
          .in('id', marketIds);
        
        // Create a map of market_id -> total_volume
        const volumeMap = new Map((volumeData || []).map((v: any) => [v.id, v.total_volume || 0]));
        
        // Sort by total_volume descending
        sortedData = [...sortedData].sort((a: any, b: any) => {
          const aVolume = volumeMap.get(a.id) || 0;
          const bVolume = volumeMap.get(b.id) || 0;
          return bVolume - aVolume;
        });
      }

      // Fetch user's watchlist in parallel
      const watchlistIds = await fetchUserWatchlist();

      const transformedPublicMarkets = await Promise.all(
        sortedData.map(async market => {
          const displayMarket = await marketEngine.toDisplayMarket(market as any);
          if (displayMarket) {
            // Add watchlist status
            displayMarket.is_watchlisted = watchlistIds.has(market.id);
          }
          return displayMarket;
        })
      );

      const finalPublicMarkets: Market[] = transformedPublicMarkets
        .filter((m): m is Market => m !== null)
        .filter((m) => m.status === 'active');

      // Cache the results
      if (page === 1) {
        marketCache.set(cacheKey, {
          data: finalPublicMarkets,
          timestamp: Date.now()
        });
      } else if (cached) {
        marketCache.set(cacheKey, {
          data: [...cached.data, ...finalPublicMarkets],
          timestamp: Date.now()
        });
      }

      setMarketData(prev => ({
        ...prev,
        publicMarkets: reset ? finalPublicMarkets : [...prev.publicMarkets, ...finalPublicMarkets]
      }));

      setUiState(prev => ({
        ...prev,
        loading: false,
        hasMore: finalPublicMarkets.length === MARKETS_PER_PAGE,
        currentPage: page
      }));

      // Only fetch featured markets on initial load
      if (page === 1 && reset) {
        await fetchFeaturedMarkets();
      }

    } catch (error) {
      logger.error('Error fetching markets:', error);
      setUiState(prev => ({ ...prev, loading: false }));
    }
  }, [uiState.selectedCategory, uiState.sortBy, user?.id, debouncedSearchQuery, getCacheKey, fetchUserWatchlist]);

  // Separate featured markets fetching
  const fetchFeaturedMarkets = useCallback(async () => {
    try {
      const { data: featuredData, error: featuredError } = await supabase
        .from('markets')
        .select(`
          id,
          title,
          description,
          category,
          status,
          is_private,
          is_verified,
          created_at,
          resolution_time,
          image_url,
          created_by,
          is_dynamic,
          total_bets,
          volume,
          total_pool,
          updated_at,
          unique_traders,
          price_change_24h,
          winning_option_id,
          engine_type,
          lmsr_b,
          market_options!market_id(id, name, status, created_at, updated_at, outstanding_shares, image_url),
          market_pools(total_amount, market_option_id)
        `)
        .eq('status', 'active')
        .eq('is_featured', true)
        .eq('is_private', false)
        .eq('is_verified', true)
        .limit(5);

      if (featuredError) throw featuredError;

      // Fetch user's watchlist in parallel
      const watchlistIds = await fetchUserWatchlist();

      const transformedFeaturedMarkets = await Promise.all(
        (featuredData || []).map(async market => {
          const displayMarket = await marketEngine.toDisplayMarket(market as any);
          if (displayMarket) {
            // Add watchlist status
            displayMarket.is_watchlisted = watchlistIds.has(market.id);
          }
          return displayMarket;
        })
      );

      const finalFeaturedMarkets: Market[] = transformedFeaturedMarkets
        .filter((m): m is Market => m !== null)
        .filter((m) => m.status === 'active');
      
      setMarketData(prev => ({
        ...prev,
        featuredMarkets: finalFeaturedMarkets
      }));

    } catch (error) {
      logger.error('Error fetching featured markets:', error);
    }
  }, [fetchUserWatchlist]);

  // Public list only; search and most sorts happen in fetchMarketsOptimized
  const filteredMarkets = useMemo(() => {
    const marketsToFilter = marketData.publicMarkets.filter((m) => m.status === 'active');

    if (uiState.sortBy === 'volume') {
      return marketsToFilter;
    }

    return [...marketsToFilter].sort((a, b) => {
      switch (uiState.sortBy) {
        case 'volume':
          return (b?.totalPool || 0) - (a?.totalPool || 0);
        case 'ending': {
          const parseRes = (m: typeof a) => {
            if (!m.resolution_time?.trim()) return Number.POSITIVE_INFINITY;
            const t = new Date(m.resolution_time).getTime();
            return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
          };
          return parseRes(a) - parseRes(b);
        }
        case 'newest':
        default:
          return new Date(b?.created_at || 0).getTime() - new Date(a?.created_at || 0).getTime();
      }
    });
  }, [marketData.publicMarkets, uiState.sortBy]);

  // Carousel navigation with infinite loop
  const goToSlide = useCallback((index: number) => {
    if (marketData.featuredMarkets.length <= 1) return;
    setIsTransitioning(true);
    setActiveCarouselIndex(index);
  }, [marketData.featuredMarkets.length]);

  const nextSlide = useCallback(() => {
    if (marketData.featuredMarkets.length <= 1) return;
    setIsTransitioning(true);
    setActiveCarouselIndex(prev => prev + 1);
  }, [marketData.featuredMarkets.length]);

  const prevSlide = useCallback(() => {
    if (marketData.featuredMarkets.length <= 1) return;
    setIsTransitioning(true);
    setActiveCarouselIndex(prev => prev - 1);
  }, [marketData.featuredMarkets.length]);
  
  // Handle infinite loop reset
  useEffect(() => {
    if (marketData.featuredMarkets.length <= 1) return;
    if (!isTransitioning) return;
    
    // When we reach the cloned slide at the end, jump to the real first slide
    if (activeCarouselIndex >= marketData.featuredMarkets.length) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setActiveCarouselIndex(0);
        setTimeout(() => setIsTransitioning(true), 50);
      }, 500);
      return () => clearTimeout(timer);
    }
    // When we reach the cloned slide at the start, jump to the real last slide
    else if (activeCarouselIndex < 0) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        setActiveCarouselIndex(marketData.featuredMarkets.length - 1);
        setTimeout(() => setIsTransitioning(true), 50);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [activeCarouselIndex, marketData.featuredMarkets.length, isTransitioning]);

  // Optimized watchlist handler
  const handleWatchlistChange = useCallback((marketId: string, isWatchlisted: boolean) => {
    setMarketData(prev => ({
      ...prev,
      publicMarkets: prev.publicMarkets.map(market =>
        market.id === marketId ? { ...market, is_watchlisted: isWatchlisted } : market
      ),
      featuredMarkets: prev.featuredMarkets.map(market =>
        market.id === marketId ? { ...market, is_watchlisted: isWatchlisted } : market
      ),
    }));
  }, []);

  // Effect for initial load and tab changes
  useEffect(() => {
    const checkConnection = async () => {
      const isConnected = await checkSupabaseConnection();
      setUiState(prev => ({ ...prev, connectionError: !isConnected }));
    };
    checkConnection();
  }, []);

  useEffect(() => {
    const loadCategories = async () => {
      const fetchedCategories = await fetchMarketCategories();
      setCategories(fetchedCategories);
    };
    loadCategories();
  }, []);

  useEffect(() => {
    setUiState(prev => ({ ...prev, loading: true, currentPage: 1, hasMore: true }));
    fetchMarketsOptimized(1, true);
  }, [uiState.selectedCategory, debouncedSearchQuery, user?.id, fetchMarketsOptimized]);

  // Carousel autoplay - simple and clean
  useEffect(() => {
    if (marketData.featuredMarkets.length <= 1 || isCarouselPaused) {
      return;
    }

    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [marketData.featuredMarkets.length, isCarouselPaused, nextSlide]);

  // Infinite scroll effect - use refs to avoid stale closures
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const hasMoreRef = useRef(uiState.hasMore);
  const isLoadingRef = useRef(uiState.loading);
  const currentPageRef = useRef(uiState.currentPage);
  const isFetchingRef = useRef(false); // Prevent duplicate fetches
  
  // Keep refs in sync with state
  useEffect(() => {
    hasMoreRef.current = uiState.hasMore;
    isLoadingRef.current = uiState.loading;
    currentPageRef.current = uiState.currentPage;
    
    console.log('Infinite scroll state:', {
      hasMore: uiState.hasMore,
      loading: uiState.loading,
      currentPage: uiState.currentPage,
      totalMarkets: marketData.publicMarkets.length
    });
  }, [uiState.hasMore, uiState.loading, uiState.currentPage, marketData.publicMarkets.length]);
  
  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    
    // Use a small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      const currentRef = loadMoreRef.current;
      if (!currentRef) {
        console.log('loadMoreRef is not set after timeout');
        return;
      }
      
      console.log('Setting up IntersectionObserver for element:', currentRef);
      
      observer = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          console.log('Intersection detected:', {
            isIntersecting: firstEntry.isIntersecting,
            hasMore: hasMoreRef.current,
            isLoading: isLoadingRef.current,
            isFetching: isFetchingRef.current,
            currentPage: currentPageRef.current
          });
          
          if (firstEntry.isIntersecting && hasMoreRef.current && !isLoadingRef.current && !isFetchingRef.current) {
            console.log('Loading more markets, page:', currentPageRef.current + 1);
            isFetchingRef.current = true;
            
            // Directly update state and fetch
            setUiState(prev => ({ ...prev, loading: true }));
            fetchMarketsOptimized(currentPageRef.current + 1, false).finally(() => {
              isFetchingRef.current = false;
            });
          }
        },
        { threshold: 0, rootMargin: '200px' }
      );
      
      observer.observe(currentRef);
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      if (observer) {
        console.log('Disconnecting IntersectionObserver');
        observer.disconnect();
      }
    };
  }, [marketData.publicMarkets.length, fetchMarketsOptimized]);

  if (uiState.connectionError) {
    return (
      <div className="py-8">
        <div className="text-center text-red-600">
          Unable to connect to the server. Please check your internet connection and try again.
        </div>
      </div>
    );
  }

  if (uiState.loading && marketData.publicMarkets.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-wayja-teal"></div>
            <p className="mt-4 text-gray-600">Loading markets...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SEO
        title="Markets"
        description="Browse and trade on prediction markets for politics, sports, entertainment, and more. Place trades on real-world events and win."
        url="/markets"
      />
      <main className="flex-1 px-2 py-2 md:px-4 md:py-3">
        
        {/* Hero Section - Featured Markets Carousel */}
        {marketData.featuredMarkets.length > 0 && (
          <div 
            className="relative mb-4"
            onMouseEnter={() => setIsCarouselPaused(true)}
            onMouseLeave={() => setIsCarouselPaused(false)}
          >
            {/* Carousel container */}
            <div className="relative overflow-hidden rounded-2xl">
              <div className="relative h-auto md:h-[380px]">
                <div 
                  className="relative flex h-auto items-start md:absolute md:inset-0 md:h-full md:items-stretch"
                  style={{
                    transform: `translateX(-${(activeCarouselIndex + 1) * (100 / (marketData.featuredMarkets.length + 2))}%)`,
                    transition: isTransitioning ? 'transform 500ms cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
                    width: `${(marketData.featuredMarkets.length + 2) * 100}%`,
                    willChange: 'transform',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden'
                  }}
                >
                  {/* Clone of last slide (for seamless backward wrapping) */}
                  <div
                    className="h-auto md:h-full flex-shrink-0 px-1"
                    style={{ 
                      width: `${100 / (marketData.featuredMarkets.length + 2)}%`,
                      willChange: 'transform',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden'
                    }}
                  >
                    <FeaturedMarketCard market={marketData.featuredMarkets[marketData.featuredMarkets.length - 1]} onWatchlistChange={handleWatchlistChange} />
                  </div>
                  
                  {/* Real slides */}
                  {marketData.featuredMarkets.map((market, index) => (
                    <div
                      key={market?.id || index}
                      className="h-auto md:h-full flex-shrink-0 px-1"
                      style={{ 
                        width: `${100 / (marketData.featuredMarkets.length + 2)}%`,
                        willChange: 'transform',
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden'
                      }}
                    >
                      <FeaturedMarketCard market={market} onWatchlistChange={handleWatchlistChange} />
                    </div>
                  ))}
                  
                  {/* Clone of first slide (for seamless forward wrapping) */}
                  <div
                    className="h-auto md:h-full flex-shrink-0 px-1"
                    style={{ 
                      width: `${100 / (marketData.featuredMarkets.length + 2)}%`,
                      willChange: 'transform',
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden'
                    }}
                  >
                    <FeaturedMarketCard market={marketData.featuredMarkets[0]} onWatchlistChange={handleWatchlistChange} />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Carousel Controls - Outside the card */}
            {marketData.featuredMarkets.length > 1 && (
              <div className="flex items-center justify-between mt-3 px-1">
                {/* Left: Navigation arrows */}
                <div className="flex items-center gap-2">
                  <button 
                    type="button"
                    onClick={prevSlide}
                    className="p-2.5 rounded-full bg-gray-100 hover:bg-wayja-teal hover:text-white text-gray-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-wayja-teal/50"
                    aria-label="Previous market"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button 
                    type="button"
                    onClick={nextSlide}
                    className="p-2.5 rounded-full bg-gray-100 hover:bg-wayja-teal hover:text-white text-gray-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-wayja-teal/50"
                    aria-label="Next market"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>

                {/* Center: Indicator dots */}
                <div className="flex items-center gap-2">
                  {marketData.featuredMarkets.map((_, index) => {
                    const isActive = (activeCarouselIndex % marketData.featuredMarkets.length) === index || 
                      (activeCarouselIndex === -1 && index === marketData.featuredMarkets.length - 1) ||
                      (activeCarouselIndex === marketData.featuredMarkets.length && index === 0);
                    return (
                      <button 
                        type="button"
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`transition-all duration-300 rounded-full ${
                          isActive 
                            ? 'w-8 h-2 bg-wayja-teal' 
                            : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                        }`}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    );
                  })}
                </div>

                {/* Right: Autoplay control */}
                <button 
                  type="button"
                  onClick={() => setIsCarouselPaused(prev => !prev)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    isCarouselPaused 
                      ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' 
                      : 'bg-wayja-teal/10 text-wayja-teal hover:bg-wayja-teal/20'
                  }`}
                  aria-label={isCarouselPaused ? "Resume autoplay" : "Pause autoplay"}
                >
                  {isCarouselPaused ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                        <path fillRule="evenodd" d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z" clipRule="evenodd" />
                      </svg>
                      <span className="hidden sm:inline">Play</span>
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                        <path fillRule="evenodd" d="M6.75 5.25a.75.75 0 01.75-.75H9a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H7.5a.75.75 0 01-.75-.75V5.25zm7.5 0A.75.75 0 0115 4.5h1.5a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H15a.75.75 0 01-.75-.75V5.25z" clipRule="evenodd" />
                      </svg>
                      <span className="hidden sm:inline">Pause</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Add bounce animation class */}
        <style>
          {`
          @keyframes subtle-bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-3px); }
          }
          .animate-bounce-subtle {
            animation: subtle-bounce 2s infinite;
          }
          `}
        </style>
        
        {/* Main Content */}
        <div className="py-2">
          {/* Leaderboard Section - Only show when promo is active */}
        {promoConfig?.is_active && promoConfig?.show_leaderboard && (
          <div className="hidden md:block mb-4 space-y-4">
            {/* Profit Leaderboard */}
            {(promoConfig?.show_profit_leaderboard ?? true) && (
              <Leaderboard limit={10} showDetails={true} />
            )}
            {/* Affiliate Leaderboard */}
            {(promoConfig?.show_affiliate_leaderboard ?? true) && (
              <AffiliateLeaderboard limit={10} showDetails={true} />
            )}
          </div>
        )}

              {/* Top Filtering Section - Polymarket Style */} 
              <div className="bg-white rounded-lg shadow-sm mb-3">
                {/* Category Filter Pills - Always Visible */}
                <div className="border-b border-gray-200">
                  <div className="overflow-x-auto">
                    <div className="flex py-1.5 px-2 gap-1.5 md:py-2 md:px-3 md:gap-2 min-w-max">
                      <button
                        onClick={() => {
                          setUiState(prev => ({ ...prev, selectedCategory: null, sortBy: 'volume' }));
                        }}
                        className="px-3 py-1.5 rounded-full text-sm font-medium transition-colors"
                        style={uiState.selectedCategory === null && uiState.sortBy === 'volume' ? activeButtonStyle : inactiveButtonStyle}
                      >
                        Trending
                      </button>
                      <button
                        onClick={() => {
                          setUiState(prev => ({ ...prev, selectedCategory: null, sortBy: 'newest' }));
                        }}
                        className="px-3 py-1.5 rounded-full text-sm font-medium transition-colors"
                        style={uiState.selectedCategory === null && uiState.sortBy === 'newest' ? activeButtonStyle : inactiveButtonStyle}
                      >
                        New
                      </button>
                      {/* {user && (
                        <button
                          onClick={() => setShowWatchlistOnly(!showWatchlistOnly)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center ${
                            showWatchlistOnly
                              ? 'bg-wayja-teal text-white'
                              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }`}
                        >
                          <Star className="w-3.5 h-3.5 mr-1" />
                          Watchlist
                        </button>
                      )} */}
                      {categories.map(category => (
                        <button
                          key={category.id}
                          onClick={() => setUiState(prev => ({ ...prev, selectedCategory: category.name === uiState.selectedCategory ? null : category.name }))}
                          className="px-3 py-1.5 rounded-full text-sm font-medium transition-colors"
                          style={uiState.selectedCategory === category.name ? activeButtonStyle : inactiveButtonStyle}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Filters Toolbar */}
                <div className="p-2 md:p-3">
                    {/* Desktop: search + sort always visible */}
                    <div className="hidden md:flex md:items-center md:gap-3">
                      <div className="relative md:flex-none md:w-[64rem]">
                        <input
                          type="text"
                          placeholder="Search markets..."
                          value={uiState.searchQuery}
                          onChange={(e) => setUiState(prev => ({ ...prev, searchQuery: e.target.value }))}
                          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-wayja-teal/20 focus:border-wayja-teal bg-gray-50 focus:bg-white transition-colors"
                        />
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      </div>
                      <div className="flex flex-1 justify-end items-center">
                        <div className="relative md:min-w-[10rem]">
                          <button
                            onClick={() => setUiState(prev => ({ ...prev, showFilters: !prev.showFilters }))}
                            className="w-auto inline-flex items-center justify-between px-3 py-2 border border-gray-200 text-sm rounded-lg text-gray-700 bg-gray-50 hover:bg-white focus:bg-white focus:outline-none focus:ring-2 focus:ring-wayja-teal/20 transition-colors"
                          >
                            <span className="flex items-center gap-1.5">
                              <span className="text-xs text-gray-400">Sort:</span>
                              <span className="font-medium">
                                {uiState.sortBy === 'newest' ? 'Newest' :
                                  uiState.sortBy === 'volume' ? 'Volume' :
                                  'Ending'}
                              </span>
                            </span>
                            <ChevronDown className={`ml-1 h-4 w-4 shrink-0 text-gray-400 transition-transform ${uiState.showFilters ? 'rotate-180' : ''}`} />
                          </button>
                          {uiState.showFilters && (
                            <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                              <div className="py-1">
                                <button
                                  onClick={() => setUiState(prev => ({ ...prev, sortBy: 'newest', showFilters: false }))}
                                  className={`w-full px-4 py-2.5 text-left text-sm ${uiState.sortBy === 'newest' ? 'font-medium' : 'text-gray-700'} hover:bg-gray-50`}
                                  style={uiState.sortBy === 'newest' ? { backgroundColor: '#5cb3ad20', color: '#5cb3ad' } : undefined}
                                >
                                  Newest first
                                </button>
                                <button
                                  onClick={() => setUiState(prev => ({ ...prev, sortBy: 'volume', showFilters: false }))}
                                  className={`w-full px-4 py-2.5 text-left text-sm ${uiState.sortBy === 'volume' ? 'font-medium' : 'text-gray-700'} hover:bg-gray-50`}
                                  style={uiState.sortBy === 'volume' ? { backgroundColor: '#5cb3ad20', color: '#5cb3ad' } : undefined}
                                >
                                  Highest volume
                                </button>
                                <button
                                  onClick={() => setUiState(prev => ({ ...prev, sortBy: 'ending', showFilters: false }))}
                                  className={`w-full px-4 py-2.5 text-left text-sm ${uiState.sortBy === 'ending' ? 'font-medium' : 'text-gray-700'} hover:bg-gray-50`}
                                  style={uiState.sortBy === 'ending' ? { backgroundColor: '#5cb3ad20', color: '#5cb3ad' } : undefined}
                                >
                                  Ending soon
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Mobile: collapsed search or sort + search icon */}
                    <div className="md:hidden">
                      {isMobileSearchOpen ? (
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Search markets..."
                            value={uiState.searchQuery}
                            onChange={(e) => setUiState(prev => ({ ...prev, searchQuery: e.target.value }))}
                            className="w-full pl-9 pr-10 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-wayja-teal/20 focus:border-wayja-teal bg-gray-50 focus:bg-white transition-colors"
                            autoFocus
                          />
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                          <button
                            type="button"
                            onClick={() => setIsMobileSearchOpen(false)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                            aria-label="Close search"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex justify-between items-center w-full gap-2">
                          <div className="relative flex-1">
                            <button
                              onClick={() => setUiState(prev => ({ ...prev, showFilters: !prev.showFilters }))}
                              className="w-full inline-flex items-center justify-between px-3 py-2 border border-gray-200 text-sm rounded-lg text-gray-700 bg-gray-50 hover:bg-white focus:bg-white focus:outline-none focus:ring-2 focus:ring-wayja-teal/20 transition-colors"
                            >
                              <span className="flex items-center gap-1.5">
                                <span className="text-xs text-gray-400">Sort:</span>
                                <span className="font-medium">
                                  {uiState.sortBy === 'newest' ? 'Newest' :
                                    uiState.sortBy === 'volume' ? 'Volume' :
                                    'Ending'}
                                </span>
                              </span>
                              <ChevronDown className={`ml-1 h-4 w-4 shrink-0 text-gray-400 transition-transform ${uiState.showFilters ? 'rotate-180' : ''}`} />
                            </button>
                            {uiState.showFilters && (
                              <div className="absolute left-0 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                                <div className="py-1">
                                  <button
                                    onClick={() => setUiState(prev => ({ ...prev, sortBy: 'newest', showFilters: false }))}
                                    className={`w-full px-4 py-2.5 text-left text-sm ${uiState.sortBy === 'newest' ? 'font-medium' : 'text-gray-700'} hover:bg-gray-50`}
                                    style={uiState.sortBy === 'newest' ? { backgroundColor: '#5cb3ad20', color: '#5cb3ad' } : undefined}
                                  >
                                    Newest first
                                  </button>
                                  <button
                                    onClick={() => setUiState(prev => ({ ...prev, sortBy: 'volume', showFilters: false }))}
                                    className={`w-full px-4 py-2.5 text-left text-sm ${uiState.sortBy === 'volume' ? 'font-medium' : 'text-gray-700'} hover:bg-gray-50`}
                                    style={uiState.sortBy === 'volume' ? { backgroundColor: '#5cb3ad20', color: '#5cb3ad' } : undefined}
                                  >
                                    Highest volume
                                  </button>
                                  <button
                                    onClick={() => setUiState(prev => ({ ...prev, sortBy: 'ending', showFilters: false }))}
                                    className={`w-full px-4 py-2.5 text-left text-sm ${uiState.sortBy === 'ending' ? 'font-medium' : 'text-gray-700'} hover:bg-gray-50`}
                                    style={uiState.sortBy === 'ending' ? { backgroundColor: '#5cb3ad20', color: '#5cb3ad' } : undefined}
                                  >
                                    Ending soon
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => setIsMobileSearchOpen(true)}
                            className="shrink-0 p-2 border border-gray-200 rounded-lg text-gray-600 bg-gray-50 hover:bg-white hover:text-wayja-teal transition-colors"
                            aria-label="Search markets"
                          >
                            <Search className="w-5 h-5" />
                          </button>
                        </div>
                      )}
                    </div>
                </div>
              </div>

              {/* All Markets */} 
              <div>
                {/* <h2 className="text-xl font-bold text-gray-900 mb-4">All Markets</h2> */}
                
                {filteredMarkets.length === 0 ? (
                  <div className="bg-white rounded-lg shadow text-center p-6">
                    <div className="mx-auto w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <Search className="w-7 h-7 text-gray-400" />
                    </div>
                    <h3 className="text-base font-medium text-gray-900 mb-1">No markets found</h3>
                    <p className="text-gray-600 mb-3 text-sm">
                      We couldn't find any markets matching your search criteria.
                    </p>
                    <button
                      onClick={() => {
                        setUiState(prev => ({
                          ...prev,
                          searchQuery: '',
                          selectedCategory: null,
                          sortBy: 'volume',
                          showWatchlistOnly: false,
                          showFilters: false
                        }));
                      }}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-gray-50 hover:bg-gray-100"
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                      {filteredMarkets.map((market) => (
                          <MarketCard 
                            key={market.id} 
                            market={market} 
                            isAdmin={false}
                            onWatchlistChange={handleWatchlistChange}
                          />
                      ))}
                    </div>
                    
                    {/* Infinite scroll trigger - always rendered for observer */}
                    <div 
                      ref={loadMoreRef} 
                      className="flex justify-center py-4 min-h-[60px]"
                    >
                      {uiState.loading && uiState.hasMore && (
                        <div className="flex flex-col items-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wayja-teal"></div>
                          <p className="mt-2 text-sm text-gray-600">Loading more markets... (Page {uiState.currentPage})</p>
                        </div>
                      )}
                      {!uiState.loading && uiState.hasMore && (
                        <p className="text-sm text-gray-400">Scroll to load more markets</p>
                      )}
                      {!uiState.hasMore && filteredMarkets.length > 0 && (
                        <p className="text-sm text-gray-500">No more markets to load</p>
                      )}
                    </div>
                  </>
                )}
              </div>
        </div>
      </main>
    </div>
  );
}
